// src/components/notifications/TeddyNudge.tsx
//
// Toast that fires 800ms after a new notification arrives, sitting
// just above the floating Teddy bottom-right. Click → opens the
// agent chat with a pre-filled "Tell me about this notification"
// message. Auto-dismisses after 8s.
//
// The toast subscribes to the same useNotifications hook the bell
// does, so they pulse together. We use a ref to compare the
// pulseAt timestamp and only show a nudge for *new* arrivals
// (not on initial mount when the user signs in).

import { useEffect, useRef, useState } from "react";
import { X, MessageCircle } from "lucide-react";
import { useNotifications } from "@/lib/notifications";
import { useAuth } from "@/components/auth/AuthContext";
import { openAgent } from "@/lib/agent/agentControl";
import { cn } from "@/lib/cn";

const SHOW_DELAY_MS = 800;
const AUTO_DISMISS_MS = 8000;

interface Nudge {
  id: number;
  title: string;
  body: string;
  link: string;
  kind: string;
}

export function TeddyNudge() {
  const auth = useAuth();
  const { items, pulseAt, markRead } = useNotifications(
    auth.isAuthenticated, 30_000,
  );
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const [closing, setClosing] = useState(false);
  const shownAt = useRef<number | null>(null);
  const lastPulseSeen = useRef<number>(0);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    if (!pulseAt || pulseAt === lastPulseSeen.current) return;
    lastPulseSeen.current = pulseAt;
    const newest = items.find((n) => !n.read_at);
    if (!newest) return;
    // Delay the toast so the eye catches the bell first.
    if (showTimer.current) clearTimeout(showTimer.current);
    showTimer.current = setTimeout(() => {
      setClosing(false);
      setNudge({
        id: newest.id, title: newest.title, body: newest.body,
        link: newest.link, kind: newest.kind,
      });
      shownAt.current = Date.now();
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    }, SHOW_DELAY_MS);
  // items in deps so we always show the latest unread one
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseAt, items, auth.isAuthenticated]);

  useEffect(() => () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
  }, []);

  function dismiss() {
    setClosing(true);
    setTimeout(() => {
      setNudge(null);
      setClosing(false);
    }, 200);
  }

  async function open() {
    if (!nudge) return;
    // Mark the notification as read so the bell stops glowing.
    await markRead([nudge.id]).catch(() => {});
    // Open the agent chat. The composer is empty; the operator
    // can type their own question (e.g. "tell me about this
    // shipment" or "explain what 'out_for_delivery' means").
    // We also stash a short prefill in sessionStorage so any
    // chat that wants to read it (today the ChatKit composer
    // doesn't, but a future integration might) can.
    try {
      sessionStorage.setItem("teddy:pending-prefill",
        `Tell me about this notification: "${nudge.title}" — ${nudge.body}`);
    } catch { /* private mode, ignore */ }
    openAgent();
    dismiss();
  }

  if (!auth.isAuthenticated) return null;
  if (!nudge) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="teddy-nudge"
      className={cn(
        "fixed bottom-24 right-4 z-40 max-w-[320px]",
        "rounded-lg border border-border bg-bg-elevated shadow-2xl",
        closing ? "teddy-nudge-out" : "teddy-nudge-in",
      )}
    >
      <div className="flex items-start gap-2 p-3">
        <div className="mt-0.5 grid h-7 w-7 place-items-center rounded-full
                        bg-accent-indigo/15 text-accent-indigo">
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-510 text-fg-primary">
            New notification
          </p>
          <p className="mt-0.5 truncate text-sm font-510 text-fg-primary">
            {nudge.title}
          </p>
          {nudge.body && (
            <p className="mt-0.5 line-clamp-2 text-xs text-fg-tertiary">
              {nudge.body}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={open}
              className="rounded-md bg-accent-indigo px-2.5 py-1 text-xs
                         font-510 text-white hover:bg-accent-indigo/90"
            >
              Ask Teddy
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-xs text-fg-tertiary hover:text-fg-primary"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-fg-quaternary hover:text-fg-primary"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
